import { useParams, Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { newsList } from '@/mocks/news';
import EmptyState from '@/components/base/EmptyState';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = newsList.find((n) => n.id === id);

  useSeo(
    news ? `${news.title} - 妖精动漫` : '动漫资讯 - 妖精动漫',
    news ? news.summary : '该资讯不存在或已删除。',
    news ? news.tags.join(',') : '动漫资讯,妖精动漫',
  );

  if (!news) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="该资讯信息不存在或已删除"
          description="你访问的资讯可能已被移除，或者链接有误。返回资讯首页看看其他内容吧。"
          actionText="返回资讯首页"
          actionTo="/news"
        />
      </div>
    );
  }

  const related = newsList.filter((n) => n.id !== news.id && (n.category === news.category || n.tags.some((t) => news.tags.includes(t)))).slice(0, 4);

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-foreground-400 mb-5">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="hover:text-primary-600 transition-colors cursor-pointer">动漫资讯</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-700 line-clamp-1">{news.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
        <article className="lg:col-span-2 min-w-0">
          <span className="inline-block px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs mb-3">{news.category}</span>
          <h1 className="text-xl md:text-3xl font-bold text-foreground-950 leading-snug">{news.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-400 pb-5 border-b border-background-200">
            <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-line"></i>{news.date}</span>
            {news.author && <span className="inline-flex items-center gap-1.5"><i className="ri-user-line"></i>{news.author}</span>}
          </div>

          <div className="mt-6 space-y-5">
            {news.content.map((block, i) => {
              if (block.type === 'heading') {
                return (
                  <h2 key={i} className="text-lg md:text-xl font-bold text-foreground-950 pt-2">{block.text}</h2>
                );
              }
              if (block.type === 'image') {
                return (
                  <figure key={i} className="w-full">
                    <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                      <img src={block.image} alt={block.caption || news.title} title={`${news.title} - 妖精动漫`} className="w-full h-full object-top" />
                    </div>
                    {block.caption && <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>}
                  </figure>
                );
              }
              return <p key={i} className="text-sm md:text-base text-foreground-700 leading-8">{block.text}</p>;
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {news.tags.map((tag) => (
              <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer">
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-8">
            <Link to="/news" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>返回资讯列表
            </Link>
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200 bg-primary-50">
              <i className="ri-links-line text-primary-600"></i>
              <h2 className="text-base font-bold text-foreground-950">相关资讯</h2>
            </div>
            {related.length > 0 ? (
              <ul className="divide-y divide-background-200">
                {related.map((n) => (
                  <li key={n.id}>
                    <Link to={`/news/${n.id}`} className="block px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 hover:text-primary-600 transition-colors">{n.title}</h3>
                      <span className="mt-1.5 inline-block text-xs text-foreground-400">{n.date}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-6 text-sm text-foreground-400">暂无相关资讯</p>
            )}
          </div>

          <div className="mt-5 rounded-lg bg-primary-600 p-5">
            <h3 className="text-base font-bold text-background-50">想看更多作品？</h3>
            <p className="mt-2 text-sm text-background-200 leading-relaxed">前往动漫分类，用多维筛选找到你感兴趣的动漫。</p>
            <Link to="/category" className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-background-50 text-primary-700 text-sm font-semibold hover:bg-accent-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
              浏览动漫分类<i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}